import { EyeOutlined, FileImageOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Table, Typography, Spin, Tag, Button, Card, Space, Modal, message, List, Divider, Input, Avatar } from "antd";

const { Text } = Typography;

const PayoutHistory = () => {
  const [loading, setLoading] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [paginationLimit, setPaginationLimit] = useState(10);
  const [pageSizeOptions] = useState(["10", "20", "50", "100"]);

  const [userPayoutHistory, setUserPayoutHistory] = useState([]);
  const [userTotalCount, setUserTotalCount] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userPaginationLimit, setUserPaginationLimit] = useState(10);

  useEffect(() => {
    fetchPayoutHistory();
  }, [page, paginationLimit]);

  useEffect(() => {
    if (isModalVisible) {
      fetchUserPayoutHistory();
    }
  }, [isModalVisible]);

  const fetchPayoutHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}payout/admin/payout-history?payoutStatus=paid&page=${page}&limit=${paginationLimit}`,
        {
          headers: {
            "x-product-type": "PIP_GRID",
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      if (response?.data?.success) {
        const data = response?.data?.payload?.data.map(
          ({ _id, user, createdAt, payoutAmount, payoutMethod, transactionProof, commissionType, bankDetails }) => ({
            key: _id,
            id: _id,
            createdAt: dayjs(createdAt).format("DD-MM-YYYY"),
            name: user?.fullName,
            email: user?.email,
            phoneNumber: user?.phoneNumber,
            totalCommission: payoutAmount,
            payoutMethod,
            transactionProof,
            commissionType,
            bankDetails,
          })
        );
        setPayoutHistory(data || []);
        setTotalCount(response?.data?.payload?.count || 0);
      }
    } catch (error) {
      console.error("Error fetching payout history:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPayoutHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`payout/admin/payout-users?payoutRequestId=${selectedPayout}&page=${userPage}&limit=${userPaginationLimit}`, {
        headers: {
          "x-product-type": "PIP_GRID",
        },
      });
      if (response?.success) {
        const data = response?.payload?.commissions?.map((item) => ({
          key: item?._id,
          id: item?._id,
          name: item?.name,
          email: item.email,
          commissionAmount: item?.commissionAmount,
          phoneNumber: item?.phone,
        }));
        setUserPayoutHistory(data || []);
        setUserTotalCount(response?.payload?.count || 0);
      }
    } catch (error) {
      console.error("Error fetching payout history:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    { title: "Commission Type", dataIndex: "commissionType", key: "commissionType" },
    {
      title: "Total Commission",
      dataIndex: "totalCommission",
      key: "totalCommission",
      render: (amount) => `$${parseFloat(amount).toFixed(2)}`,
    },
    {
      title: "Payout Method",
      dataIndex: "payoutMethod",
      key: "payoutMethod",
    },
    {
      title: "Transaction Proof",
      key: "transactionProof",
      align: "center",
      width: 120,
      render: (_, record) => {
        return record.transactionProof ? (
          <div
            onClick={() => {
              setCurrentImage(record.transactionProof);
              setIsImageModalVisible(true);
            }}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
              color: "#1890ff",
              padding: "8px 0",
            }}
          >
            <span style={{ fontSize: "12px", marginTop: "4px" }}>View Proof</span>
          </div>
        ) : (
          <span style={{ color: "#999" }}>No proof</span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => {
        return (
          <EyeOutlined
            style={{ cursor: "pointer", fontSize: "16px", color: "#1890ff" }}
            onClick={() => {
              setSelectedPayout(record?.id);
              setIsModalVisible(true);
            }}
          />
        );
      },
    },
  ];

  const userColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    {
      title: "Commission Amount",
      dataIndex: "commissionAmount",
      key: "commissionAmount",
    },
  ];

  return (
    <div className="p-4">
      <Spin spinning={loading} size="large">
        <Typography.Title level={3} className="page-title">
          Payout History
        </Typography.Title>
        <Table
          columns={columns}
          dataSource={payoutHistory}
          loading={loading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: paginationLimit,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions,
            onChange: (page, pageSize) => {
              setPage(page);
              setPaginationLimit(pageSize);
            },
          }}
        />
        <Modal title="Transaction Proof" open={isImageModalVisible} onCancel={() => setIsImageModalVisible(false)} footer={null} width={800}>
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <img
              src={currentImage}
              alt="Transaction Proof"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            />
          </div>
        </Modal>

        <Modal title="Payout Details" open={isModalVisible} onCancel={() => setIsModalVisible(false)} footer={null} width={800}>
          {selectedPayout && (
            <div>
              <Table
                columns={userColumns}
                dataSource={userPayoutHistory || []}
                rowKey="userId"
                pagination={{
                  current: userPage,
                  pageSize: userPaginationLimit,
                  total: userTotalCount,
                  onChange: (page, pageSize) => {
                    setPage(page);
                  },
                }}
              />
            </div>
          )}
        </Modal>
      </Spin>
    </div>
  );
};

export default PayoutHistory;
